// ============================================
// components/auth/ForgotPassword.js
// ============================================
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  AppBar, Toolbar, Typography, Container, TextField, 
  Button, Paper, Box, CssBaseline, Stack 
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { authService } from '../../services/authService';
import { ROUTES } from '../../utils/constants';
import theme from '../../styles/theme';

const ForgotPassword = () => {

  const [email, setEmail] = useState('');
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handlePasswordReset = async (e) => {
    e.preventDefault();

    if (!email) {
      setError("Email is required");
      return;
    }

    setLoading(true);
    const result = await authService.resetPassword(email);
    setLoading(false);

    if (result.success) {
      setMessage("Password reset email sent! Please check your inbox.");
      setError(null);
    } else {
      setError(result.error);
      setMessage(null);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar component="header" position="sticky" color="transparent" elevation={0} sx={{
        backdropFilter: 'blur(8px)',
        backgroundColor: 'rgba(17, 24, 39, 0.4)',
        borderBottom: '1px solid rgba(255,255,255,0.06)'
      }}>
        <Toolbar variant="dense" sx={{ minHeight: 48, px: 2 }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Porchlight
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button color="inherit" component={RouterLink} to={ROUTES.LOGIN}>
            Login
          </Button>
        </Toolbar>
      </AppBar>
      <Container component="main" aria-labelledby="forgot-page-title" maxWidth="xs" sx={{ px: 2 }}>
        {/* Visually hidden page heading for accessibility (inside main landmark) */}
        <Box component="h1" id="forgot-page-title" sx={{
          border: 0,
          clip: 'rect(0 0 0 0)',
          height: 1,
          margin: -1,
          overflow: 'hidden',
          padding: 0,
          position: 'absolute',
          whiteSpace: 'nowrap',
          width: 1
        }}>
          Forgot password - Porchlight
        </Box>
        <Paper elevation={0} variant="glass" sx={{
          mt: 8,
          px: 3,
          py: 3
        }}>
          <Stack spacing={2} alignItems="stretch">
            <Typography variant="h5" component="h2" align="center" sx={{ fontWeight: 700 }}>
              Reset your password
            </Typography>
            <Typography id="forgot-form-title" variant="h6" component="h3" align="center" sx={{ fontWeight: 600 }}>
              Forgot password
            </Typography>
          </Stack>
          <Box component="form" aria-labelledby="forgot-form-title" onSubmit={handlePasswordReset} sx={{ mt: 2 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              type="email"
              inputMode="email"
              InputLabelProps={{ sx: { color: 'text.primary' } }}
            />
            {message && <Typography color="primary">{message}</Typography>}
            {error && <Typography color="error">{error}</Typography>}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Reset Password'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </ThemeProvider>
  );

};

export default ForgotPassword;